import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare, ExternalLink, Users } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLOR = {
    online: "#22c55e",
    idle: "#facc15",
    dnd: "#ef4444",
    offline: "#6b7280",
};

export default function DiscordWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const fetchOnce = async () => {
            try {
                const { data } = await api.get("/discord-widget");
                if (mounted) setData(data);
            } catch {
                if (mounted) setData({ enabled: false });
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchOnce();
        // Refresh every 60s so the online count stays fresh
        const id = setInterval(fetchOnce, 60_000);
        return () => {
            mounted = false;
            clearInterval(id);
        };
    }, []);

    if (loading || !data || !data.enabled) return null;

    const presenceCount = data.presence_count ?? 0;
    const members = data.members || [];

    return (
        <motion.div
            data-testid="discord-widget"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="panel-yellow p-6 md:p-8 mb-12 relative overflow-hidden"
        >
            {/* Discord-purple accent strip */}
            <div
                className="absolute top-0 left-0 w-full h-[3px]"
                style={{
                    background:
                        "linear-gradient(90deg, #5865F2 0%, #8E96FF 50%, #5865F2 100%)",
                }}
            />

            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex items-center gap-4 lg:flex-1">
                    <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: "#5865F222", color: "#5865F2" }}
                    >
                        <MessageSquare size={24} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1">
                            Discord · serwer społeczności
                        </div>
                        <div className="text-white text-xl md:text-2xl font-display tracking-tight truncate">
                            {data.name || "Discord"}
                        </div>
                        <div
                            data-testid="discord-online"
                            className="text-[color:var(--text-secondary)] text-sm flex items-center gap-2 mt-1"
                        >
                            <span
                                className="inline-block w-2 h-2 rounded-full bg-[#22c55e] animate-pulse"
                                aria-hidden="true"
                            />
                            <Users size={14} className="text-[color:var(--primary)]" />
                            <span>
                                <strong className="text-white">
                                    {presenceCount}
                                </strong>{" "}
                                online
                            </span>
                        </div>
                    </div>
                </div>

                {/* Avatars cluster */}
                {members.length > 0 && (
                    <div
                        className="flex items-center -space-x-2 shrink-0"
                        aria-hidden="true"
                    >
                        {members.slice(0, 8).map((m) => (
                            <div
                                key={m.id}
                                title={m.username}
                                className="relative w-9 h-9 rounded-full border-2 border-[color:var(--bg-base)] overflow-hidden bg-[#5865F2]/30 flex items-center justify-center text-xs text-white font-semibold"
                            >
                                {m.avatar_url ? (
                                    <img
                                        src={m.avatar_url}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    (m.username || "?")
                                        .slice(0, 2)
                                        .toUpperCase()
                                )}
                                <span
                                    className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-[color:var(--bg-base)]"
                                    style={{
                                        background:
                                            STATUS_COLOR[m.status] ||
                                            STATUS_COLOR.online,
                                    }}
                                />
                            </div>
                        ))}
                        {members.length > 8 && (
                            <div className="relative w-9 h-9 rounded-full border-2 border-[color:var(--bg-base)] bg-white/5 flex items-center justify-center text-[10px] text-[color:var(--text-secondary)]">
                                +{Math.max(0, presenceCount - 8)}
                            </div>
                        )}
                    </div>
                )}

                {data.instant_invite && (
                    <a
                        data-testid="discord-join-btn"
                        href={data.instant_invite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold uppercase tracking-wider text-xs rounded-[4px] text-white shrink-0 transition-transform hover:scale-[1.02]"
                        style={{ background: "#5865F2" }}
                    >
                        Dołącz do Discord
                        <ExternalLink size={12} />
                    </a>
                )}
            </div>
        </motion.div>
    );
}
